import { Modal, View } from 'react-native';
import CardDetails from './components/CardDetails';
import { Credential } from '../../../types/credentials';
import CredentialCard from './CredentialCard';
import { animated, useSpring } from '@react-spring/web';
import { useState, useEffect } from 'react';
import CardActions from './components/CardActions';
import Layout from '../../../components/layout/Layout';
import HeaderButton from '../../../components/layout/HeaderButton';
import { mdiArrowLeft } from '@mdi/js';
import { getFluidSpacing } from '../../../helpers/utils';

interface CardDetailsModalProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  credential: Credential;
}

/**
 * @description Card details modal
 * @param {CardDetailsModalProps} {
 *   isVisible,
 *   setIsVisible,
 *   credential,
 *   primaryAction,
 *   secondaryAction
 * }
 * @return {React.FC}
 */
const CardDetailsModal: React.FC<CardDetailsModalProps> = ({
  isVisible,
  setIsVisible,
  credential
}) => {
  // Add state to control actual modal visibility
  const [modalVisible, setModalVisible] = useState(isVisible);

  const fadeAnimation = useSpring({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateX(40px)' : 'translateX(-40px)',
    config: { mass: 1, tension: 800, friction: 50 },
    onRest: () => {
      // When animation finishes and modal is not visible, actually close the modal
      if (!isVisible) {
        setModalVisible(false);
      }
    }
  });

  // When isVisible becomes true, immediately show modal
  useEffect(() => {
    if (isVisible) {
      setModalVisible(true);
    }
  }, [isVisible]);

  const handleClose = () => {
    // Start fade animation
    setIsVisible(false);
    // Modal will be actually closed after animation finishes (in onRest)
  };

  return (
    <Modal onRequestClose={handleClose} visible={modalVisible} animationType="none">
      <Layout
        title="Card Details"
        showFooter={false}
        leftButton={<HeaderButton icon={mdiArrowLeft} onPress={handleClose} />}>
        <animated.div
          style={{
            ...fadeAnimation,
            height: '100%',
            width: '100%'
          }}>
          <View
            style={{
              flex: 1,
              flexDirection: 'column',
              width: '100%',
              transform: [{ translateX: -40 }],
              paddingVertical: getFluidSpacing(12, 16, 20)
            }}>
            {/* Fixed Header Section */}
            <View
              style={{
                flexShrink: 0,
                marginBottom: getFluidSpacing(8, 10, 12)
              }}>
              <CredentialCard
                credential={credential}
                _detailsModal={true}
                _detailsModalClose={handleClose}
              />
            </View>

            {/* Scrollable Content Section */}
            <View
              style={{
                flex: 1,
                minHeight: 0,
                paddingVertical: getFluidSpacing(12, 16, 20),
                marginBottom: getFluidSpacing(8, 10, 12)
              }}>
              <CardDetails credential={credential} />
            </View>

            {/* Fixed Footer Section */}
            <View
              style={{
                flexShrink: 0
              }}>
              <CardActions credential={credential} onRemoveCredential={handleClose} />
            </View>
          </View>
        </animated.div>
      </Layout>
    </Modal>
  );
};

export default CardDetailsModal;
